/**
 * Editor drag and drop composable
 * Handles drag and drop functionality for contenteditable editor
 */

import { ref } from 'vue';

/**
 * @param {Object} options
 * @param {import('vue').Ref<HTMLElement>} options.editorRef - Editor element ref
 * @param {Function} options.syncContent - Sync content callback
 * @returns {Object} Drag and drop handlers and state
 */
export function useEditorDrag(options = {}) {
  const { editorRef, syncContent, onFileDrop } = options;

  // Drag state
  const dragState = ref({
    isDragging: false,
    isExternalDragging: false,
    draggedElement: null,
    dropIndicator: null,
    dropTarget: null,
    dropPosition: null, // 'before' | 'after'
    dropRange: null, // Range for text position
  });

  // RAF throttle state for drag over
  let dragOverRAF = null;
  let lastDragX = 0;
  let lastDragY = 0;

  // Cached drop indicator element
  let cachedIndicator = null;

  /**
   * Create drop indicator element (reuses cached indicator)
   * @returns {HTMLElement}
   */
  const createDropIndicator = () => {
    if (!cachedIndicator) {
      cachedIndicator = document.createElement('span');
      cachedIndicator.className = 'drop-indicator';
      cachedIndicator.setAttribute('data-drop-indicator', 'true');
    }
    return cachedIndicator;
  };

  /**
   * Remove drop indicator from DOM (keeps cached reference)
   */
  const removeDropIndicators = () => {
    if (cachedIndicator && cachedIndicator.parentNode) {
      cachedIndicator.parentNode.removeChild(cachedIndicator);
    }
  };

  /**
   * Get draggable element from target
   * @param {HTMLElement} el - Target element
   * @returns {HTMLElement|null}
   */
  const getDraggableElement = (el) => {
    while (el && el !== editorRef?.value) {
      if (
        el.getAttribute &&
        (el.getAttribute('data-type') === 'image' ||
          el.getAttribute('data-type') === 'document' ||
          el.getAttribute('data-type') === 'tag' ||
          el.getAttribute('data-type') === 'remix' ||
          el.getAttribute('data-type') === 'userskill')
      ) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  };

  /**
   * Get caret range from point coordinates
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {Range|null}
   */
  const getCaretRangeFromPoint = (x, y) => {
    // Standard method
    if (document.caretRangeFromPoint) {
      return document.caretRangeFromPoint(x, y);
    }
    // Firefox method
    if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(x, y);
      if (pos) {
        const range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
        return range;
      }
    }
    return null;
  };

  /**
   * Handle drag start event
   * @param {DragEvent} e
   */
  const handleDragStart = (e) => {
    const draggableEl = getDraggableElement(e.target);
    if (!draggableEl) return;

    dragState.value.isDragging = true;
    dragState.value.draggedElement = draggableEl;

    // Set drag data
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');

    // Add dragging style immediately
    draggableEl.classList.add('dragging');
    draggableEl.style.opacity = '0.5';
  };

  /**
   * Core drag over logic (called via RAF)
   * @param {number} clientX
   * @param {number} clientY
   * @param {HTMLElement} target
   */
  const processDragOver = (clientX, clientY, target) => {
    const draggableEl = getDraggableElement(target);

    // If dragging over self, don't process
    if (draggableEl === dragState.value.draggedElement) {
      removeDropIndicators();
      dragState.value.dropTarget = null;
      dragState.value.dropRange = null;
      return;
    }

    // If found droppable image/tag target
    if (draggableEl) {
      const rect = draggableEl.getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      const position = clientX < midX ? 'before' : 'after';

      // Only update indicator when position changes
      if (dragState.value.dropTarget !== draggableEl || dragState.value.dropPosition !== position) {
        removeDropIndicators();

        const indicator = createDropIndicator();
        if (position === 'before') {
          draggableEl.parentNode.insertBefore(indicator, draggableEl);
        } else {
          draggableEl.parentNode.insertBefore(indicator, draggableEl.nextSibling);
        }

        dragState.value.dropTarget = draggableEl;
        dragState.value.dropPosition = position;
        dragState.value.dropRange = null;
      }
    } else {
      // Drop at text position - use caretRangeFromPoint for precise position
      const range = getCaretRangeFromPoint(clientX, clientY);

      if (range && editorRef?.value?.contains(range.startContainer)) {
        // Check if inside dragged element
        let node = range.startContainer;
        let isInsideDraggedElement = false;
        while (node && node !== editorRef?.value) {
          if (node === dragState.value.draggedElement) {
            isInsideDraggedElement = true;
            break;
          }
          node = node.parentNode;
        }

        if (!isInsideDraggedElement) {
          // Check if is drop indicator
          if (
            range.startContainer.nodeType === Node.ELEMENT_NODE &&
            range.startContainer.hasAttribute &&
            range.startContainer.hasAttribute('data-drop-indicator')
          ) {
            return;
          }

          removeDropIndicators();

          // Insert indicator at cursor position
          const indicator = createDropIndicator();
          range.insertNode(indicator);

          dragState.value.dropTarget = null;
          dragState.value.dropPosition = null;
          // Save indicator reference
          dragState.value.dropRange = {
            container: indicator.parentNode,
            indicator: indicator,
          };
        }
      } else {
        removeDropIndicators();
        dragState.value.dropTarget = null;
        dragState.value.dropPosition = null;
        dragState.value.dropRange = null;
      }
    }
  };

  /**
   * Handle drag over event (throttled with RAF)
   * @param {DragEvent} e
   */
  const handleDragOver = (e) => {
    const isFileDrag = e.dataTransfer.types.includes('Files');
    if (!dragState.value.isDragging && !isFileDrag) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // If it's a file drag and we're not already dragging internally
    if (isFileDrag && !dragState.value.isDragging) {
      dragState.value.isDragging = true;
      dragState.value.isExternalDragging = true;
    }

    const clientX = e.clientX;
    const clientY = e.clientY;
    const target = e.target;

    // Skip if position hasn't changed significantly (5px threshold)
    const dx = Math.abs(clientX - lastDragX);
    const dy = Math.abs(clientY - lastDragY);
    if (dx < 5 && dy < 5) return;

    lastDragX = clientX;
    lastDragY = clientY;

    // Cancel pending RAF and schedule new one
    if (dragOverRAF) {
      cancelAnimationFrame(dragOverRAF);
    }

    dragOverRAF = requestAnimationFrame(() => {
      processDragOver(clientX, clientY, target);
      dragOverRAF = null;
    });
  };

  /**
   * Handle drag leave event
   * @param {DragEvent} e
   */
  const handleDragLeave = (e) => {
    // Check if actually left the editor
    if (!editorRef?.value?.contains(e.relatedTarget)) {
      removeDropIndicators();
      if (dragState.value.isExternalDragging) {
        resetDragState();
      } else {
        dragState.value.dropTarget = null;
        dragState.value.dropPosition = null;
        dragState.value.dropRange = null;
      }
    }
  };

  /**
   * Ensure spacing around element
   * @param {HTMLElement} element
   */
  const ensureSpacing = (element) => {
    // Check if space needed before
    const prevNode = element.previousSibling;
    const needSpaceBefore =
      !prevNode ||
      (prevNode.nodeType === Node.TEXT_NODE &&
        prevNode.textContent.length > 0 &&
        !prevNode.textContent.endsWith(' ') &&
        !prevNode.textContent.endsWith('\u00A0')) ||
      (prevNode.nodeType === Node.ELEMENT_NODE && !prevNode.hasAttribute('data-drop-indicator'));

    if (
      needSpaceBefore &&
      (!prevNode || prevNode.nodeType !== Node.TEXT_NODE || prevNode.textContent.length === 0)
    ) {
      const space = document.createTextNode('\u00A0');
      element.parentNode.insertBefore(space, element);
    }

    // Check if space needed after
    const nextNode = element.nextSibling;
    const needSpaceAfter =
      !nextNode ||
      (nextNode.nodeType === Node.TEXT_NODE &&
        nextNode.textContent.length > 0 &&
        !nextNode.textContent.startsWith(' ') &&
        !nextNode.textContent.startsWith('\u00A0')) ||
      (nextNode.nodeType === Node.ELEMENT_NODE && !nextNode.hasAttribute('data-drop-indicator'));

    if (
      needSpaceAfter &&
      (!nextNode || nextNode.nodeType !== Node.TEXT_NODE || nextNode.textContent.length === 0)
    ) {
      const space = document.createTextNode('\u00A0');
      element.parentNode.insertBefore(space, element.nextSibling);
    }
  };

  /**
   * Reset drag state
   */
  const resetDragState = () => {
    // Cancel any pending RAF
    if (dragOverRAF) {
      cancelAnimationFrame(dragOverRAF);
      dragOverRAF = null;
    }

    // Reset position tracking
    lastDragX = 0;
    lastDragY = 0;

    dragState.value = {
      isDragging: false,
      isExternalDragging: false,
      draggedElement: null,
      dropIndicator: null,
      dropTarget: null,
      dropPosition: null,
      dropRange: null,
    };
  };

  /**
   * Handle drop event
   * @param {DragEvent} e
   */
  const handleDrop = (e) => {
    e.preventDefault();

    if (!dragState.value.isDragging) {
      return;
    }

    const isFileDrop = e.dataTransfer.files && e.dataTransfer.files.length > 0;

    if (isFileDrop) {
      const dropTarget = dragState.value.dropTarget;
      const dropPosition = dragState.value.dropPosition;
      const dropRange = dragState.value.dropRange;

      let insertionPoint = null;

      // Determine where to insert
      if (dropTarget) {
        insertionPoint = {
          target: dropTarget,
          position: dropPosition,
        };
      } else if (dropRange && dropRange.indicator) {
        insertionPoint = {
          target: dropRange.indicator,
          position: 'before',
        };
      }

      // Call file drop handler
      onFileDrop?.(e.dataTransfer.files, insertionPoint);

      removeDropIndicators();
      resetDragState();
      return;
    }

    if (!dragState.value.draggedElement) {
      removeDropIndicators();
      resetDragState();
      return;
    }

    const draggedEl = dragState.value.draggedElement;
    const dropTarget = dragState.value.dropTarget;
    const dropPosition = dragState.value.dropPosition;
    const dropRange = dragState.value.dropRange;

    // Remove dragging style
    draggedEl.classList.remove('dragging');
    draggedEl.style.opacity = '';

    let moved = false;

    // Method 1: Drop beside other image/tag
    if (dropTarget && dropTarget !== draggedEl) {
      if (dropPosition === 'before') {
        dropTarget.parentNode.insertBefore(draggedEl, dropTarget);
      } else {
        dropTarget.parentNode.insertBefore(draggedEl, dropTarget.nextSibling);
      }
      moved = true;
    }
    // Method 2: Drop at text position (indicator position)
    else if (dropRange && dropRange.indicator) {
      const indicator = dropRange.indicator;
      // Insert dragged element at indicator position
      indicator.parentNode.insertBefore(draggedEl, indicator);
      moved = true;
    }

    if (moved) {
      // Ensure spacing between elements
      ensureSpacing(draggedEl);
      syncContent?.({ immediate: true });
    }

    // Cleanup
    removeDropIndicators();
    resetDragState();
  };

  /**
   * Handle drag end event
   * @param {DragEvent} e
   */
  const handleDragEnd = (e) => {
    const draggableEl = getDraggableElement(e.target);
    if (draggableEl) {
      draggableEl.classList.remove('dragging');
      draggableEl.style.opacity = '';
    }

    removeDropIndicators();
    resetDragState();
  };

  /**
   * Make element draggable
   * @param {HTMLElement} element
   */
  const makeDraggable = (element) => {
    element.setAttribute('draggable', 'true');
    element.classList.add('draggable-item');
  };

  return {
    dragState,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    makeDraggable,
    removeDropIndicators,
    resetDragState,
  };
}
